import React from "react";
import ProfileCard from "../components/ProfileCard";
import SEO from "../components/SEO";
import profilesData from "../data/profiles.json";
import { Profile, defaultProfile } from "../types";

function getProfileById(id: string): Profile {
  const profiles: Profile[] = profilesData as Profile[];
  const foundProfile = profiles.find((profile) => profile.id === id);
  return foundProfile || defaultProfile;
}

const PWCskinV1: React.FC = () => {
  return (
    <div className="bg-gray-900 text-white">
      <SEO
        title="PWC Skin V1 - Neural Amp Modeler Visual Overhaul"
        description="Give your Neural Amp Modeler a complete visual overhaul with our handcrafted skin interface for NAM."
        image="https://www.pwc-profiles.com/images/plugins/pwc_nam_skin/pwc_nam_skin_screenshot_amp.webp"
        url="https://www.pwc-profiles.com/plugins/pwcskinv1"
      />
      <section className="bg-cover bg-center flex justify-center pt-4">
        <div className="text-center pt-20 px-10 rounded-lg max-w-[800px] flex flex-col items-center">
          <h1 className="text-5xl font-bold gradient-text max-w-lg">
            The Amp You Know, The Look You&apos;ve Always Wanted.
          </h1>
          <p className="mt-10 text-xl px-10 max-w-lg">
            Give your Neural Amp Modeler a complete visual overhaul with our
            stunning new skin.
          </p>
          <img
            className="mb-[-10%] mt-16 w-full max-w-[800px]"
            src="/images/plugins/pwc_nam_skin/nam_skin_window_laptop.png"
            alt="Photo of amp with PWC skin for NAM"
          />
        </div>
      </section>

      <section className="pt-20 bg-gray-100">
        <p className="mt-4 text-lg max-w-3xl mx-auto px-10 md:mt-20 text-gray-800 text-center">
          It looks, feels, and acts like a real amp, putting crucial information
          right at your fingertips. It&apos;s time to make your digital tone
          look as good as it sounds.
        </p>
      </section>

      <section className="bg-cover bg-center bg-gray-100 t-10">
        <div className="container mx-auto text-center items-center flex flex-col bg-opacity-70 p-10 rounded-lg text-gray-800">
          <h2 className="text-4xl font-bold mt-10">
            Meticulously Crafted Design
          </h2>
          <p className="mt-4 text-lg max-w-3xl mx-auto">
            We didn&apos;t just re-skin the plugin. We created a user
            experience. Every knob, every texture, and every detail has been
            carefully designed to evoke the feeling of a real piece of gear. The
            photorealistic look is more than just eye candy. It&apos;s designed
            to inspire creativity and make you feel more connected to your tone.
          </p>
        </div>
      </section>

      <div
        className="w-full bg-cover bg-center min-h-48 bg-gray-100 pb-10"
        style={{
          backgroundImage:
            "url('/images/plugins/pwc_nam_skin/iso_view_bright_transparent.webp')",
        }}
      >
        <img
          className="w-full opacity-0"
          src="/images/plugins/pwc_nam_skin/iso_view_bright_transparent.webp"
          alt="Photo of amp with PWC skin for NAM"
        />
      </div>

      <section
        className="bg-cover lg:flex lg:justify-end bg-center lg:py-4 md:items-center w-full lg:h-[800px]"
        style={{
          backgroundImage:
            "url('/images/plugins/pwc_nam_skin/distance_v2.jpg')",
        }}
      >
        <div className="container p-10 mt-10 w-full lg:w-1/2 bg-gray-900 lg:bg-transparent max-w-full">
          <h3 className="text-3xl font-bold mb-8 text-center gradient-text">
            Regular NAM Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-lg">
            <div>
              <h4 className="font-bold text-xl mb-2">
                State-of-the-Art Modeling
              </h4>
              <p>
                Harness the power of deep learning to create incredibly accurate
                digital models of real-world amplifiers, pedals, and gear.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-xl mb-2">Built-in IR Loader</h4>
              <p>
                Pair your amp models with your favorite speaker cabinet impulse
                responses for a complete and customizable signal chain.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-xl mb-2">Essential Tone Shaping</h4>
              <p>
                Dial in your sound with a simple tone stack EQ, a noise gate,
                and input and output controls.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cover bg-center py-10 w-full items-center justify-center flex">
        <div className="container p-10 w-full max-w-[800px] flex flex-col">
          <h3 className="text-3xl font-bold mb-8 text-center gradient-text">
            Bonus Features with our New Skin
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-lg">
            <div>
              <h4 className="font-bold text-xl mb-2">High-Fidelity Design</h4>
              <p>
                A meticulously crafted, photorealistic interface that brings the
                aesthetic of a physical amp to your screen.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-xl mb-2">
                Enhanced Metadata Display
              </h4>
              <p>
                Stop analyzing file names.
                <br />
                Our skin instantly displays the model name, creator, and tone
                type right on the interface.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-xl mb-2">Intuitive Gear Icons</h4>
              <p>
                Quickly identify the gear type, whether it&apos;s an amp,
                preamp, or a full amp plus cab rig, with a glance at the new
                icons.
              </p>
            </div>
          </div>

          <h4 className="font-bold text-xl mb-2 mt-20 text-center">
            Gear Icons
          </h4>
          <div
            className="grid grid-cols-2 md:grid-cols-3 place-items-center gap-4 py-8"
            id="icon-container"
          >
            <img
              src="/images/plugins/pwc_nam_skin/icon_amp.svg"
              alt="Amp Icon"
              className="h-24"
            />
            <img
              src="/images/plugins/pwc_nam_skin/icon_preamp.svg"
              alt="Preamp Icon"
              className="h-24"
            />
            <img
              src="/images/plugins/pwc_nam_skin/icon_pedal.svg"
              alt="Pedal Icon"
              className="h-24"
            />
            <img
              src="/images/plugins/pwc_nam_skin/icon_amp_cab.svg"
              alt="Amp Cab Icon"
              className="h-24"
            />
            <img
              src="/images/plugins/pwc_nam_skin/icon_studio.svg"
              alt="Studio Icon"
              className="h-24"
            />
            <img
              src="/images/plugins/pwc_nam_skin/icon_amp_pedal.svg"
              alt="Amp Pedal Icon"
              className="h-24"
            />
          </div>
        </div>
      </section>

      <section className="pt-20 bg-gray-800">
        <div className="container mx-auto text-center p-8 max-w-[800px]">
          <h2 className="text-4xl font-bold">Availability</h2>
          <p className="mt-4 text-lg">
            The new skin is available for the Neural Amp Modeler as a VST3
            plugin and a standalone application for Windows.
            <br />
            <br />
            <span className="text-red-700">
              The plugin is still in a testing phase. If you want to give it a
              try, please follow the link below.
            </span>
          </p>
          <div className="flex justify-center mt-8 sm:w-1/2 mx-auto">
            <ProfileCard profile={getProfileById("pwc-nam-skin")} />
          </div>
        </div>
      </section>

      <section className="bg-gray-900">
        <div className="container flex flex-col items-center justify-center mx-auto text-center p-8 gap-8 max-w-[800px]">
          <h2 className="text-4xl font-bold pb-4 pt-20">Media</h2>
          <iframe
            width="100%"
            height="auto"
            style={{ aspectRatio: "16 / 9" }}
            src="https://www.youtube.com/embed/WtDBrt_0TG8?si=HWRO3eOZSFqiSemX"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          ></iframe>
          <img
            src="/images/plugins/pwc_nam_skin/pwc_nam_skin_screenshot_pedal.webp"
            alt="PWC skin screenshot pedal"
            className="w-full"
          />
          <img
            src="/images/plugins/pwc_nam_skin/pwc_nam_skin_screenshot_back.webp"
            alt="PWC skin screenshot back"
            className="w-full"
          />
        </div>
      </section>
    </div>
  );
};

export default PWCskinV1;
